import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

// Single Source of Truth for authorization remains has_permission() on
// the server (RLS/triggers) -- this hook is a READ-ONLY, UX-layer mirror
// of that exact same rule set (project_members.user_id/project_id/is_active,
// role organization_id matching project's organization_id OR NULL,
// role_permissions.granted = true), fetched via ONE nested-select query
// per (projectId, user) pair instead of one RPC call per can() check.
// Server-side enforcement (has_permission() inside triggers) remains
// authoritative regardless of what this hook returns -- this hook must
// NEVER be treated as a security boundary, only as UX convenience.
export function useProjectAuth(projectId) {
  const { user } = useAuth()
  const [permissions, setPermissions] = useState(new Set())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!projectId || !user?.id) { setPermissions(new Set()); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    ;(async () => {
      // Single nested-select: project_members -> roles -> role_permissions,
      // filtered exactly like has_permission()'s own WHERE clause.
      const { data, error } = await supabase
        .from('project_members')
        .select(`
          is_active,
          projects!inner ( organization_id ),
          roles!inner (
            organization_id,
            role_permissions ( permission, granted )
          )
        `)
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle()

      if (cancelled) return
      if (error || !data) { setPermissions(new Set()); setLoading(false); return }

      // Role organization scope check -- identical condition to
      // has_permission()'s JOIN: (role.organization_id = project.organization_id OR role.organization_id IS NULL)
      const roleOrgValid = data.roles.organization_id === data.projects.organization_id || data.roles.organization_id === null
      if (!roleOrgValid) { setPermissions(new Set()); setLoading(false); return }

      const granted = (data.roles.role_permissions || [])
        .filter(rp => rp.granted === true)
        .map(rp => rp.permission)
      setPermissions(new Set(granted))
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [projectId, user?.id])

  const can = (permission) => permissions.has(permission)
  return { can, loading }
}
